import { Injectable, NgZone } from '@angular/core';
import { INotification } from 'ngsild-connector';
import { INgsiMapBaseEntity } from '../ngsild/interfaces/ngsi-entity.interface';
import { ILeafletInstance } from './leaflet.interface';

@Injectable({ providedIn: 'root' })
export class EntityBatchingService {
  private readonly BATCH_INTERVAL = 1000;
  private readonly MAX_BATCH_PROCESS = 100;
  private readonly BATCHABLE_TYPES = [
    'FusionMaritimeTarget',
    'MaritimeTargets',
  ];

  private readonly batchedTemporaryEntities = new Map<string, {
    entities: Map<string, { entity: INgsiMapBaseEntity; notification: INotification; timestamp: number }>,
    timer: ReturnType<typeof setTimeout> | null,
    instanceId: string
  }>();

  private readonly activeInstances = new Set<string>();

  constructor(private ngZone: NgZone) {}

  processEntity(
      entity: INgsiMapBaseEntity,
      notification: INotification,
      appPluginWidget: any,
      instance: ILeafletInstance,
      handleNotificationCallback: (widget: any, notification: INotification, instance: ILeafletInstance) => void
  ): void {
    this.activeInstances.add(instance.id);
    const entityType = entity.type;

    if (this.BATCHABLE_TYPES.includes(entityType)) {
      this.addToTemporaryBatch(entity, notification, appPluginWidget, instance, handleNotificationCallback);
    } else {
      handleNotificationCallback(appPluginWidget, notification, instance);
    }
  }

  private addToTemporaryBatch(
      entity: INgsiMapBaseEntity,
      notification: INotification,
      appPluginWidget: any,
      instance: ILeafletInstance,
      handleNotificationCallback: (widget: any, notification: INotification, instance: ILeafletInstance) => void
  ): void {
    const batchKey = `${instance.id}_temp`;
    const currentAdditionalCount = instance.additionalEntities.size;
    const maxAdditionalEntities = this.getMaxAdditionalEntities(instance);

    if (currentAdditionalCount >= maxAdditionalEntities) {
      console.warn(`additionalEntities limit reached for map ${instance.id}. Current: ${currentAdditionalCount}, Max: ${maxAdditionalEntities}`);
      return;
    }

    if (!this.batchedTemporaryEntities.has(batchKey)) {
      this.batchedTemporaryEntities.set(batchKey, {
        entities: new Map(),
        timer: null,
        instanceId: instance.id
      });
    }

    const batch = this.batchedTemporaryEntities.get(batchKey)!;

    batch.entities.set(entity.id, { entity, notification, timestamp: Date.now() });

    if (batch.timer === null) {
      this.ngZone.runOutsideAngular(() => {
        batch.timer = setTimeout(() => {
          this.processTemporaryBatch(batchKey, appPluginWidget, instance, handleNotificationCallback);
        }, this.BATCH_INTERVAL);
      });
    }
  }

  private processTemporaryBatch(
      batchKey: string,
      appPluginWidget: any,
      instance: ILeafletInstance,
      handleNotificationCallback: (widget: any, notification: INotification, instance: ILeafletInstance) => void
  ): void {
    const batch = this.batchedTemporaryEntities.get(batchKey);
    if (!batch || batch.entities.size === 0 || !this.activeInstances.has(instance.id)) return;

    const currentAdditionalCount = instance.additionalEntities.size;
    const maxAdditionalEntities = this.getMaxAdditionalEntities(instance);
    const availableSlots = maxAdditionalEntities - currentAdditionalCount;

    if (availableSlots <= 0) {
      console.warn(`No available slots in additionalEntities for map ${instance.id}`);
      this.clearTemporaryBatch(batchKey);
      return;
    }

    const sortedEntities = Array.from(batch.entities.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, Math.min(availableSlots, this.MAX_BATCH_PROCESS));

    let processedCount = 0;
    const processNext = () => {
      if (!this.activeInstances.has(instance.id)) {
        this.clearTemporaryBatch(batchKey);
        return;
      }

      const item = sortedEntities[processedCount];
      if (!item) {
        this.clearTemporaryBatch(batchKey);
        console.info(`[Batch] Processed ${processedCount} entities.`);
        return;
      }

      const { entity, notification } = item;
      try {
        handleNotificationCallback(appPluginWidget, notification, instance);
      } catch (error) {
        console.error(`Error processing entity ${entity.id}:`, error);
      }

      processedCount++;
      if (processedCount < sortedEntities.length) {
        requestIdleCallback(processNext);
      } else {
        this.clearTemporaryBatch(batchKey);
      }
    };

    requestIdleCallback(processNext);
  }

  private clearTemporaryBatch(batchKey: string): void {
    const batch = this.batchedTemporaryEntities.get(batchKey);
    if (batch) {
      if (batch.timer) {
        clearTimeout(batch.timer);
      }
      this.batchedTemporaryEntities.delete(batchKey);
    }
  }

  private getMaxAdditionalEntities(instance: ILeafletInstance): number {
    const baseLimit = 2000;
    const zoomLevel = instance.zoom;
    if (zoomLevel > 15) return Math.floor(baseLimit * 0.3);
    if (zoomLevel > 12) return Math.floor(baseLimit * 0.5);
    if (zoomLevel > 8) return Math.floor(baseLimit * 0.7);
    return baseLimit;
  }

  public getAdditionalEntitiesStats(instance: ILeafletInstance) {
    const current = instance.additionalEntities.size;
    const max = this.getMaxAdditionalEntities(instance);
    const utilization = (current / max) * 100;
    const availableSlots = Math.max(0, max - current);

    return {
      current,
      max,
      utilization: Math.round(utilization * 100) / 100,
      availableSlots
    };
  }

  public cleanupInstance(instanceId: string): void {
    const batchKey = `${instanceId}_temp`;
    this.clearTemporaryBatch(batchKey);
    this.activeInstances.delete(instanceId);
  }

  public getBatchStats(instanceId?: string): any {
    const stats: any = {};
    this.batchedTemporaryEntities.forEach((batch, batchKey) => {
      if (!instanceId || batch.instanceId === instanceId) {
        stats[batchKey] = {
          entityCount: batch.entities.size,
          hasTimer: batch.timer !== null,
          oldestEntity: batch.entities.size > 0 ? Math.min(...Array.from(batch.entities.values()).map(e => e.timestamp)) : null,
          newestEntity: batch.entities.size > 0 ? Math.max(...Array.from(batch.entities.values()).map(e => e.timestamp)) : null
        };
      }
    });
    return stats;
  }

  public canAcceptMoreTemporaryEntities(instance: ILeafletInstance, count: number = 1): boolean {
    const stats = this.getAdditionalEntitiesStats(instance);
    return stats.availableSlots >= count;
  }
}
